'use client';

import { useEffect, useState } from 'react';
import { Pill } from '@/components/ui/pill';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { useLocale } from 'next-intl';

type SectionHeadingProps = {
    page: string;
    align?: 'center' | 'left';
    maxWidthClass?: string;
    className?: string;
    fallbackPill?: string;
    fallbackTitle?: string;
    fallbackSubtitle?: string;
    titleVariant?: 'h2' | 'h3' | 'h4';
};

export function SectionHeading({
    page,
    align = 'center',
    maxWidthClass = 'max-w-3xl',
    className = '',
    fallbackPill,
    fallbackTitle,
    fallbackSubtitle,
    titleVariant = 'h2',
}: SectionHeadingProps) {
    const locale = useLocale();
    const [isVisible, setIsVisible] = useState(false);
    const [pill, setPill] = useState(fallbackPill || '');
    const [title, setTitle] = useState(fallbackTitle || '');
    const [subtitle, setSubtitle] = useState(fallbackSubtitle || '');

    useEffect(() => {
        // Skip API fetch if page is 'DO_NOT_FETCH' (used when backend data is already available)
        if (page === 'DO_NOT_FETCH') {
            // Use fallback values directly without API fetch
            setPill(fallbackPill || '');
            setTitle(fallbackTitle || '');
            setSubtitle(fallbackSubtitle || '');
            setIsVisible(true);
            return;
        }

        // Skip API fetch if page is empty and we have fallback values
        if (!page && fallbackTitle) {
            setPill(fallbackPill || '');
            setTitle(fallbackTitle || '');
            setSubtitle(fallbackSubtitle || '');
            setIsVisible(true);
            return;
        }

        const fetchSectionHeading = async () => {
            try {
                const res = await fetch(`/api/page-titles?page=${encodeURIComponent(page)}&locale=${encodeURIComponent(locale)}`, {
                    cache: 'no-store',
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data) {
                        setPill(data.pagePill || fallbackPill || '');
                        // Prioritize fallbackTitle when provided (for sections that need specific titles)
                        setTitle(fallbackTitle || data.title || '');
                        // Only set subtitle if fallbackSubtitle is not explicitly empty string
                        if (fallbackSubtitle !== '') {
                            setSubtitle(data.sectionSubtitle || fallbackSubtitle || '');
                        } else {
                            setSubtitle('');
                        }
                    }
                }
            } catch (error) {

            } finally {
                setIsVisible(true);
            }
        };

        fetchSectionHeading();
    }, [page, locale, fallbackPill, fallbackTitle, fallbackSubtitle]);

    if (!pill && !title && !subtitle) {
        return null;
    }

    const alignClasses = align === 'center' ? 'text-center mx-auto' : 'text-left';

    return (
        <div
            className={cn(
                `${maxWidthClass} ${alignClasses} mb-14 transform transition-all duration-700`,
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6',
                className
            )}
        >
            {pill && (
                <Pill
                    variant={align === 'left' ? 'left' : 'default'}
                    className={cn('mb-4', align === 'center' ? 'mx-auto' : 'mx-0')}
                >
                    {pill}
                </Pill>
            )}
            {title && (
                <Typography variant={titleVariant} className="mb-4">
                    {title}
                </Typography>
            )}
            {subtitle && (
                <Typography variant="p" className="text-lg">
                    {subtitle}
                </Typography>
            )}
        </div>
    );
}

